import * as React from 'react';
import {
  SessionEditorCodemirror,
  CodeMirror,
} from '../codemirror/codemirror';
import './styles.sass';

// firepad requires setting CodeMirror to global context
(window as any).CodeMirror = CodeMirror;
import * as Firepad from 'firepad';
import * as firebase from 'firebase';
import {Loader} from '../../loader/loader';
import {FIREBASE} from '../../../utils/config';

export interface ISessionEditorFirepadProps {
  editorValue: string;
  initialCode: string;
  connected: boolean;
  disabled: boolean;
  language: string;
  userId: string;
  link: string;

  disconnectEditor: () => void;
  connectEditor: () => void;
  changeCode: (value: string) => void;
}

interface ISessionEditorFirepadState {
  firePadReady: boolean;
}

export class SessionEditorFirepad extends React.Component<ISessionEditorFirepadProps, ISessionEditorFirepadState> {
  public state: ISessionEditorFirepadState = {
    firePadReady: false,
  };
  private firebaseRef: any;
  private firePad: any;
  private codeMirror: any;
  private codeMirrorRef: SessionEditorCodemirror;

  public constructor(props: ISessionEditorFirepadProps) {
    super(props);

    if (firebase.apps.length === 0) {
      firebase.initializeApp(FIREBASE);
    }
  }

  public render(): React.ReactElement<{}> {
    const {
      initialCode,
      editorValue,
      changeCode,
    } = this.props;

    return (
      <SessionEditorCodemirror
        initialCode={initialCode}
        value={editorValue}
        ref={node => this.codeMirrorRef = node}
        onChange={changeCode}
      >
        {(!this.props.link || !this.state.firePadReady) && <Loader />}
      </SessionEditorCodemirror>
    );
  }

  public componentDidMount(): void {
    this.assignFirePad();
    this.onConnected();
  }

  public componentDidUpdate(prevProps: ISessionEditorFirepadProps): void {
    const {
      initialCode,
      connected,
      disabled,
      link,
    } = prevProps;

    if (this.props.link !== link || this.props.initialCode !== initialCode) {
      this.assignFirePad();
    }

    if (disabled !== this.props.disabled || connected !== this.props.connected) {
      this.toggleCodeMirror(this.props.disabled || !this.props.connected);
    }
  }

  public componentWillUnmount(): void {
    this.destroyFirePad();
  }

  private assignFirePad(): void {
    this.destroyFirePad();

    const {language, link} = this.props;
    if (!link) {
      return;
    }

    this.setFirebaseRef(link);
    this.codeMirror = this.codeMirrorRef.define({language});
    this.toggleCodeMirror(this.props.disabled);
    this.firePad = Firepad.fromCodeMirror(this.firebaseRef, this.codeMirror, {});
    this.firePad.setUserId(this.props.userId);
    this.firePad.on('ready', this.onReady);
  }

  private toggleCodeMirror(state: boolean): void {
    if (this.codeMirror) {
      this.codeMirror.setOption('readOnly', state);
      this.codeMirror.focus();
    }
  }

  private setFirebaseRef(link: string): void {
    this.firebaseRef = firebase.database().ref(link);
  }

  private destroyFirePad(): void {
    if (this.firePad) {
      this.firePad.off('ready', this.onReady);
      this.firePad.dispose();
      this.codeMirrorRef.destroy();
      this.setState({firePadReady: false});
    }
  }

  private onReady = (): void => {
    if (this.firePad.isHistoryEmpty()) {
      /**
       * Set initial code
       *
       * @see {@link https://firepad.io/docs/|Firebase Data}
       *   a: user id
       *   o: data
       *   t: TIMESTAMP
       *
       * when multiple users connect to the application and set initial code through setText|setHtml|defaultText
       * Firepad appends items to 'history' for each user
       * - in our case initial code have to be the same for each user.
       * In Firepad there is no public API for operation which will replace first revision in 'history',
       * however there is alternative way to do it through native Firebase API.
       */
      const REVISION_ID = 'A0';
      const CHILD_ID = 'history';
      const data = {
        a: this.props.userId,
        o: [this.props.initialCode],
        t: Date.now(),
      };

      this.firebaseRef
        .child(CHILD_ID)
        .child(REVISION_ID)
        .set(data);
    }

    this.setState({firePadReady: true}, () => this.codeMirrorRef.focusCursor());
  };

  private onConnected = (): void => {
    const {
      connectEditor,
      disconnectEditor,
    } = this.props;

    firebase.database()
      .ref('.info/connected')
      .on('value', (snap) => {
        this.setState(
          {firePadReady: snap.val()},
          () => snap.val() === true ? connectEditor() : disconnectEditor(),
        );
      });
  }
}
